import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MenuItemType } from '../models/menu-item.type';

export type SidebarMode = 'none' | 'modules' | 'pages';

const SELECTED_MODULE_KEY = 'mlm_selected_module_id';

@Injectable({ providedIn: 'root' })
export class ModuleStateService {
  private _selectedModule = new BehaviorSubject<MenuItemType | null>(null);
  private _sidebarMode = new BehaviorSubject<SidebarMode>('none');
  private _allModules = new BehaviorSubject<MenuItemType[]>([]);

  selectedModule$ = this._selectedModule.asObservable();
  sidebarMode$ = this._sidebarMode.asObservable();
  allModules$ = this._allModules.asObservable();

  get selectedModule(): MenuItemType | null {
    return this._selectedModule.value;
  }

  get sidebarMode(): SidebarMode {
    return this._sidebarMode.value;
  }

  setModules(modules: MenuItemType[]): void {
    this._allModules.next(modules);
    // Attempt to restore selected module now that we have the module list
    this.restoreSelectedModule(modules);
  }

  /** Called after modules are loaded from API to re-hydrate selection from sessionStorage. */
  restoreSelectedModule(modules: MenuItemType[]): void {
    if (this._selectedModule.value) {
      return; // already selected, nothing to restore
    }
    try {
      const savedId = sessionStorage.getItem(SELECTED_MODULE_KEY);
      if (savedId) {
        const found = modules.find(m => m.id === savedId);
        if (found) {
          this._selectedModule.next(found);
          // Do NOT auto-open sidebar on reload — user opens it manually
        }
      }
    } catch {
      // sessionStorage unavailable, ignore
    }
  }

  selectModule(module: MenuItemType): void {
    this._selectedModule.next(module);
    try {
      sessionStorage.setItem(SELECTED_MODULE_KEY, module.id);
    } catch { /* ignore */ }
    // After selecting a module show the pages sidebar
    this._sidebarMode.next('pages');
  }

  openModulesSidebar(): void {
    this._sidebarMode.next(this._sidebarMode.value === 'modules' ? 'none' : 'modules');
  }

  openPagesSidebar(): void {
    if (this._selectedModule.value) {
      this._sidebarMode.next(this._sidebarMode.value === 'pages' ? 'none' : 'pages');
    }
  }

  closeSidebar(): void {
    this._sidebarMode.next('none');
  }
}
